import React, { type ComponentProps, useCallback } from 'react';
import type { TextProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { theme } from '@garnish/constants';
import { type Tabs } from 'expo-router';

import { type IconName } from '../Icon';
import { MainTabBarItem } from './MainTabBarItem';

// ─────────────────────────────────────────────────────────────────────────────

export const MainTabBarV2 = (props: Props) => {
  const {
    state,
    descriptors,
    navigation,
    tabsDisplayingBadge = [],
    ...viewProps
  } = props;

  return (
    <View style={styles.container} accessibilityRole="tablist" {...viewProps}>
      {state.routes.map((route, index) => {
        const { key, name } = route;
        const { options } = descriptors[key];

        // NOTE: Ignores internal/system tabs
        if (!options.title) return null;

        const isActive = state.index === index;
        const hasBadge = tabsDisplayingBadge.includes(name);

        return (
          <MainTabBarItemContainer
            key={key}
            screenKey={key}
            name={name}
            options={options}
            isActive={isActive}
            hasBadge={hasBadge}
            navigation={navigation}
          />
        );
      })}
    </View>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const MainTabBarItemContainer = (props: ItemContainerProps) => {
  const {
    screenKey,
    name,
    isActive,
    hasBadge,
    maxFontSizeMultiplier,
    options,
    navigation,
  } = props;

  const {
    tabBarButtonTestID,
    iconName,
    title,
    tabBarAccessibilityLabel,
    onPress,
  } = options;

  const itemTitle = title ?? name ?? '';

  // ─── Handlers ────────────────────────────────────────────────────────

  const handlePress = useCallback(() => {
    const event = navigation.emit({ type: 'tabPress', target: screenKey, canPreventDefault: true }); // prettier-ignore

    if (event.defaultPrevented) return;

    onPress?.();

    // NOTE: The `merge: true` option makes sure that the params inside the tab screen are preserved
    navigation.navigate(name, undefined, { merge: true });
  }, [name, navigation, onPress, screenKey]);

  const handleLongPress = useCallback(() => {
    navigation.emit({ type: 'tabLongPress', target: screenKey });
  }, [navigation, screenKey]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <MainTabBarItem
      testID={tabBarButtonTestID}
      title={itemTitle}
      iconName={iconName}
      accessibilityLabel={tabBarAccessibilityLabel}
      isActive={isActive}
      hasBadge={hasBadge}
      maxFontSizeMultiplier={maxFontSizeMultiplier}
      onPress={handlePress}
      onLongPress={handleLongPress}
    />
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    backgroundColor: theme.colors.APP_BACKGROUND,
    borderTopWidth: 1,
    borderTopColor: theme.colors.OPACITY.DARK_KALE.DARK,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  tabsDisplayingBadge?: readonly string[];
} & TabBarProps;

type ItemContainerProps = Readonly<{
  screenKey: string;
  name: string;
  isActive: boolean;
  hasBadge: boolean;
  navigation: TabBarProps['navigation'];
  options: TabBarProps['descriptors'][number]['options'] &
    TabBarButtonExtraProps;
  maxFontSizeMultiplier?: TextProps['maxFontSizeMultiplier'];
}>;

export type TabBarButtonExtraProps = {
  iconName?: IconName;
  onPress?: () => void;
};

type TabBarProps = Parameters<TabBarRenderer>[0];
type TabBarRenderer = NonNullable<ComponentProps<typeof Tabs>['tabBar']>;
